import { useEffect, useState } from 'react';
import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import logger from '@eneco/common/log';
import { toURL } from '@eneco/common/path';
import { stripAccolades } from '@eneco/common/string';
import { defaultValues as addressFinderDefaultValues } from '@eneco/components/AddressFinder/AddressFinder';
import RichText from '@eneco/components/RichText/RichText';
import { useFormsPublicV2PostFormV2 } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Button, Form, NotificationBox, Stack } from '@eneco/sparky';
import { FormProvider, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';

import { CorrectionReason } from './correction-invoice-form/CorrectionReason';
import { Header } from './correction-invoice-form/Header';
import { InvoiceDetails } from './correction-invoice-form/InvoiceDetails';
import { MeterReadings } from './correction-invoice-form/MeterReadings';
import { PersonalDetails } from './correction-invoice-form/PersonalDetails';
import { prepareRequestBody } from './correction-invoice-form/prepareRequestBody';
import { getSchema } from './correction-invoice-form/schema';
import type { FormValues } from './correction-invoice-form/types';
import type { CorrectionInvoiceFormContent } from './correction-invoice-form-content.types';

/**
 * `CorrectionInvoiceForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/oxxio/src/CorrectionInvoiceForm/CorrectionInvoiceForm.tsx` 1:1:
 * Oxxio's "Correctienota" request (personal and invoice details, the correction reason and the meter
 * readings), posted to the forms v2 endpoint, then redirecting to the submit link; a failure is logged
 * and shows the generic error notification. Drops the legacy `wrap()`/Experience Editor checks — this
 * lane never runs inside the Experience Editor.
 *
 * Both adapters hand every field over under the same flat name, so each section reads the content
 * directly; see `correction-invoice-form-content.types.ts` for the shapes that differ.
 */
const CorrectionInvoiceForm: FC = () => {
  const { adapter } = useCms();
  const content = useContent<CorrectionInvoiceFormContent>();
  const { send, isSuccess, isError, error, isLoading } = useFormsPublicV2PostFormV2();

  const { pushHref } = useRouter();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);
  const errorNotification = content.genericErrorNotification;

  const { label, businessUnit } = useDC();

  const resolver = typedYupResolver<FormValues>(getSchema(content, isSuffixRequired));
  const formMethods = useForm<FormValues>({
    defaultValues: addressFinderDefaultValues,
    mode: 'onBlur',
    resolver,
  });
  const { handleSubmit } = formMethods;

  const onSubmit: SubmitHandler<FormValues> = async values => {
    const formId = stripAccolades(content.senderIdOption ?? '');
    const requestBody = prepareRequestBody(values, content, formId);
    await send({
      label,
      formId,
      businessUnit,
      requestBody,
    } as Parameters<typeof send>[0]);
  };

  const submitHref = linkWithUrl(content.submitButtonLink)?.href;
  useEffect(() => {
    if (isSuccess) {
      pushHref(toURL(submitHref ?? ''));
    }
  }, [isSuccess, submitHref, pushHref]);

  useEffect(() => {
    if (!isError) return;
    logger.error('LpYJ9d', 'Failed to submit form', { error });
  }, [isError, error]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...formMethods}>
        <Stack gap={{ initial: '6', md: '10' }}>
          <Header content={content} />
          <PersonalDetails
            content={content}
            setIsSuffixRequired={setIsSuffixRequired}
            isSuffixRequired={isSuffixRequired}
          />
          <InvoiceDetails content={content} />
          <CorrectionReason content={content} />
          <MeterReadings content={content} />
          {error ? (
            <NotificationBox
              title={errorNotification?.title}
              variant={errorNotification?.variant}
              text={<RichText html={adapter.toRichText(errorNotification?.content)} />}
              isAlert
            />
          ) : null}
          <Button type="submit" isLoading={isLoading}>
            {content.submitButtonLink?.text}
          </Button>
        </Stack>
      </FormProvider>
    </Form>
  );
};

export default CorrectionInvoiceForm;
